import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Loader2, Check, Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Veuillez entrer un email valide");
      return;
    }

    setIsLoading(true);
    try {
      // Check if already subscribed
      const { data: existing } = await supabase
        .from("email_subscribers")
        .select("id")
        .eq("email", email.trim())
        .maybeSingle();

      if (existing) {
        toast.info("Cet email est déjà inscrit !");
        setSubscribed(true);
        return;
      }

      const id = crypto.randomUUID();
      const { error } = await supabase.from("email_subscribers").insert({
        id,
        email: email.trim(),
        promo_code: "NEWSLETTER",
      });

      if (error) {
        toast.error("Une erreur est survenue, veuillez réessayer");
        return;
      }

      // Send welcome email
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "newsletter-welcome",
          recipientEmail: email.trim(),
          idempotencyKey: `newsletter-welcome-${id}`,
        },
      });

      setSubscribed(true);
      toast.success("Vous êtes inscrit !");
    } catch {
      toast.error("Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  if (subscribed) {
    return (
      <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-[hsl(var(--gold)/0.08)] p-8 md:p-12 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center">
            <Check className="w-7 h-7 text-accent" />
          </div>
        </div>
        <h3 className="text-xl md:text-2xl font-display font-bold text-foreground mb-2">
          Vous êtes inscrit ! 🎉
        </h3>
        <p className="text-muted-foreground">
          Merci ! Vous recevrez nos nouveautés et exclusivités par email.
        </p>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-[hsl(var(--gold)/0.08)] p-8 md:p-12 text-center">
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
          <Bell className="w-7 h-7 text-primary" />
        </div>
      </div>
      <h3 className="text-xl md:text-2xl font-display font-bold text-foreground mb-2">
        Restez à jour ! 📬
      </h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Entrez votre email pour recevoir nos nouveautés, exclusivités et surprises en avant-première.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="votre@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
            disabled={isLoading}
          />
        </div>
        <Button type="submit" disabled={isLoading} className="whitespace-nowrap bg-gradient-to-r from-[hsl(var(--terracotta))] to-[hsl(var(--gold))] text-white hover:from-[hsl(var(--terracotta-dark))] hover:to-[hsl(var(--terracotta))]">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "S'inscrire"
          )}
        </Button>
      </form>
      <p className="text-xs text-muted-foreground mt-4">
        Pas de spam, promis ! ✨
      </p>
    </section>
  );
};
